import type { Identifier } from "ra-core";

import {
  PROPERTY_OPTION_STATUSES,
  type AppRole,
  type PropertyFurnished,
  type PropertyOperation,
  type PropertyOption,
  type PropertyOptionOrigin,
  type PropertyOptionOwner,
  type PropertyOptionStatus,
  type PropertyOptionType,
} from "../types";

export * from "./inventoryFilterRules";

/**
 * Almikia Epic EC (TASK-030): pure UI rules behind the "Opciones" screens.
 * The database stays the authority (RLS, guard triggers); these helpers only
 * decide which controls and fields to render so a role never sees something
 * the database would reject, and never asks for the owner's personal data
 * it may not read.
 */

/** Value of the responsable choice / filter that stands for the agency (AMK). */
export const AMK_CHOICE = "amk";

export const OPCION_ADDRESS_REQUIRED = "Escribe la dirección de la propiedad";
export const OPCION_TYPE_REQUIRED = "Elige el tipo de propiedad";
export const OPCION_PRICE_INVALID = "Escribe un precio de 0 o más";
export const OPCION_RESPONSABLE_REQUIRED = "Elige la responsable de la opción";
export const OPCION_OWNER_NAME_REQUIRED =
  "Escribe el nombre del propietario o deja vacío su contacto";
export const OPCION_OWNER_EMAIL_INVALID = "Escribe un correo válido";
export const OPCION_EXCLUSIVA_DATE_REQUIRED =
  "Una exclusiva necesita la fecha de firma del contrato";
export const OPCION_EXCLUSIVITY_RANGE_INVALID =
  "La vigencia no puede terminar antes de la firma del contrato";
export const OPCION_FURNISHED_REQUIRED =
  "Elige si la renta es amueblada o sin amueblar";
export const OPCION_COUNT_INVALID = "Escribe un número de 0 o más";
export const OPCION_GENERIC_ERROR =
  "No se pudo guardar el cambio. Inténtalo de nuevo.";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Roles that create options (an asesora/lider only for herself). */
const CREATE_ROLES: AppRole[] = [
  "direccion",
  "lead_manager",
  "asesora",
  "lider",
];
const ADVISOR_ROLES: AppRole[] = ["asesora", "lider"];
const MANAGER_ROLES: AppRole[] = ["direccion", "lead_manager"];

type OptionOwnership = Pick<PropertyOption, "responsable_sales_id">;

const isSameId = (
  a: Identifier | null | undefined,
  b: Identifier | null | undefined,
): boolean => a != null && b != null && String(a) === String(b);

/** True when `userId` is the advisor responsable of `option` (never true for AMK). */
const isResponsable = (
  role: AppRole,
  userId: Identifier | undefined,
  option: OptionOwnership,
): boolean =>
  ADVISOR_ROLES.includes(role) && isSameId(option.responsable_sales_id, userId);

/** Who may create an option. */
export const canCreateOption = (role: AppRole): boolean =>
  CREATE_ROLES.includes(role);

/**
 * Who sees (and is asked for) the owner's private contact: direccion,
 * lead_manager and the option's responsable. Mirrors the owner table's RLS.
 */
export const canSeeOwner = (
  role: AppRole,
  userId: Identifier | undefined,
  option: OptionOwnership,
): boolean =>
  MANAGER_ROLES.includes(role) || isResponsable(role, userId, option);

/** Who edits an existing option: the managers and the responsable. */
export const canEditOption = (
  role: AppRole,
  userId: Identifier | undefined,
  option: OptionOwnership,
): boolean =>
  MANAGER_ROLES.includes(role) || isResponsable(role, userId, option);

/**
 * The status changes offered on the detail: every status but the current one,
 * for whoever may edit the option. Withdrawal ('retirada') is a status, never a delete.
 */
export const getStatusActions = (
  role: AppRole,
  userId: Identifier | undefined,
  option: OptionOwnership & Pick<PropertyOption, "status">,
): PropertyOptionStatus[] =>
  canEditOption(role, userId, option)
    ? PROPERTY_OPTION_STATUSES.filter((status) => status !== option.status)
    : [];

/**
 * Who sees "Vender esta opción" (Almikia Epic EC, TASK-032): only on a
 * disponible option, for lead_manager, direccion and any asesora / lider
 * (including the option's own responsable, who may sell it to her own buyer
 * lead: case 1b). The server (`create_corretaje_operation`) re-checks the lead
 * before creating anything.
 */
export const canSellOption = (
  role: AppRole,
  _userId: Identifier | undefined,
  option: OptionOwnership & Pick<PropertyOption, "status">,
): boolean =>
  option.status === "disponible" &&
  (MANAGER_ROLES.includes(role) || ADVISOR_ROLES.includes(role));

export type ResponsableChoice = { id: string; name: string };

type AdvisorSale = {
  id: Identifier;
  role?: AppRole;
  advisor_code?: string | null;
  disabled?: boolean;
};

/**
 * Responsable choices: AMK plus every enabled advisor with a clave for
 * direccion / lead_manager; only herself for an asesora / lider; nothing else.
 */
export const getResponsableChoices = (
  role: AppRole,
  userId: Identifier | undefined,
  sales: readonly AdvisorSale[],
): ResponsableChoice[] => {
  const withClave = sales
    .filter(
      (sale) =>
        !sale.disabled &&
        (sale.role === "asesora" || sale.role === "lider") &&
        (sale.advisor_code ?? "").trim() !== "",
    )
    .map((sale) => ({
      id: String(sale.id),
      name: (sale.advisor_code ?? "").trim(),
    }));
  if (MANAGER_ROLES.includes(role)) {
    return [{ id: AMK_CHOICE, name: "AMK" }, ...withClave];
  }
  if (ADVISOR_ROLES.includes(role)) {
    return withClave.filter((choice) => choice.id === String(userId));
  }
  return [];
};

export interface OpcionFormValues {
  address: string;
  zone: string;
  property_type: PropertyOptionType;
  origin: PropertyOptionOrigin;
  /** ISO date, only meaningful for an exclusiva. */
  exclusivity_signed_on: string;
  exclusivity_ends_on: string;
  operation: PropertyOperation;
  /** Only meaningful (and required) for a renta. */
  furnished: PropertyFurnished | "";
  levels: string;
  bedrooms: string;
  bathrooms: string;
  area_m2: string;
  parking: string;
  price: string;
  status: PropertyOptionStatus;
  /** `AMK_CHOICE`, an advisor id as a string, or "" when not chosen. */
  responsable: string;
  owner_full_name: string;
  owner_phone: string;
  owner_email: string;
  owner_notes: string;
}

export type OpcionFormErrors = Partial<
  Record<
    | "address"
    | "property_type"
    | "exclusivity_signed_on"
    | "exclusivity_ends_on"
    | "furnished"
    | "levels"
    | "bedrooms"
    | "bathrooms"
    | "area_m2"
    | "parking"
    | "price"
    | "responsable"
    | "owner_full_name"
    | "owner_email",
    string
  >
>;

const countText = (value: number | null | undefined): string =>
  value == null ? "" : String(value);

/** Initial values: the option and (when permitted) its owner, or blanks. */
export const toOpcionFormValues = (
  option?: PropertyOption,
  owner?: PropertyOptionOwner,
  defaultResponsable = "",
): OpcionFormValues => {
  let responsable = defaultResponsable;
  if (option) {
    responsable = option.responsable_is_amk
      ? AMK_CHOICE
      : String(option.responsable_sales_id ?? "");
  }
  return {
    address: option?.address ?? "",
    zone: option?.zone ?? "",
    property_type: option?.property_type ?? "",
    origin: option?.origin ?? "opcion",
    exclusivity_signed_on: option?.exclusivity_signed_on ?? "",
    exclusivity_ends_on: option?.exclusivity_ends_on ?? "",
    operation: option?.operation ?? "venta",
    furnished: option?.furnished ?? "",
    levels: countText(option?.levels),
    bedrooms: countText(option?.bedrooms),
    bathrooms: countText(option?.bathrooms),
    area_m2: countText(option?.area_m2),
    parking: countText(option?.parking),
    price: option ? String(option.price) : "",
    status: option?.status ?? "disponible",
    responsable,
    owner_full_name: owner?.full_name ?? "",
    owner_phone: owner?.phone ?? "",
    owner_email: owner?.email ?? "",
    owner_notes: owner?.notes ?? "",
  };
};

const hasOwnerData = (values: OpcionFormValues): boolean =>
  [
    values.owner_full_name,
    values.owner_phone,
    values.owner_email,
    values.owner_notes,
  ].some((text) => text.trim() !== "");

const COUNT_FIELDS = [
  "levels",
  "bedrooms",
  "bathrooms",
  "area_m2",
  "parking",
] as const;

/** Validates the form; `includeOwner` is false when the owner block is not rendered. */
export const validateOpcionForm = (
  values: OpcionFormValues,
  includeOwner: boolean,
): OpcionFormErrors => {
  const errors: OpcionFormErrors = {};
  if (values.address.trim() === "") errors.address = OPCION_ADDRESS_REQUIRED;
  if (values.property_type.trim() === "") {
    errors.property_type = OPCION_TYPE_REQUIRED;
  }
  if (values.origin === "exclusiva") {
    if (values.exclusivity_signed_on === "") {
      errors.exclusivity_signed_on = OPCION_EXCLUSIVA_DATE_REQUIRED;
    } else if (
      values.exclusivity_ends_on !== "" &&
      values.exclusivity_ends_on < values.exclusivity_signed_on
    ) {
      errors.exclusivity_ends_on = OPCION_EXCLUSIVITY_RANGE_INVALID;
    }
  }
  if (values.operation === "renta" && values.furnished === "") {
    errors.furnished = OPCION_FURNISHED_REQUIRED;
  }
  COUNT_FIELDS.forEach((field) => {
    const text = values[field].trim();
    if (text !== "" && !(Number.isFinite(Number(text)) && Number(text) >= 0)) {
      errors[field] = OPCION_COUNT_INVALID;
    }
  });
  const price = Number(values.price);
  if (values.price.trim() === "" || !Number.isFinite(price) || price < 0) {
    errors.price = OPCION_PRICE_INVALID;
  }
  if (values.responsable === "") {
    errors.responsable = OPCION_RESPONSABLE_REQUIRED;
  }
  if (includeOwner) {
    if (hasOwnerData(values) && values.owner_full_name.trim() === "") {
      errors.owner_full_name = OPCION_OWNER_NAME_REQUIRED;
    }
    const email = values.owner_email.trim();
    if (email !== "" && !EMAIL_PATTERN.test(email)) {
      errors.owner_email = OPCION_OWNER_EMAIL_INVALID;
    }
  }
  return errors;
};

const blankToNull = (text: string): string | null => {
  const trimmed = text.trim();
  return trimmed === "" ? null : trimmed;
};

const toIdentifier = (text: string): Identifier =>
  /^\d+$/.test(text) ? Number(text) : text;

const toCount = (text: string): number | null =>
  text.trim() === "" ? null : Number(text);

/** The option record sent to the data provider (exactly one responsable). */
export const buildOpcionPayload = (
  values: OpcionFormValues,
): Pick<
  PropertyOption,
  | "address"
  | "zone"
  | "property_type"
  | "origin"
  | "exclusivity_signed_on"
  | "exclusivity_ends_on"
  | "operation"
  | "furnished"
  | "levels"
  | "bedrooms"
  | "bathrooms"
  | "area_m2"
  | "parking"
  | "price"
  | "status"
  | "responsable_sales_id"
  | "responsable_is_amk"
> => {
  const isExclusiva = values.origin === "exclusiva";
  const isRenta = values.operation === "renta";
  const isAmk = values.responsable === AMK_CHOICE;
  return {
    address: values.address.trim(),
    zone: blankToNull(values.zone),
    property_type: values.property_type,
    origin: values.origin,
    exclusivity_signed_on: isExclusiva
      ? blankToNull(values.exclusivity_signed_on)
      : null,
    exclusivity_ends_on: isExclusiva
      ? blankToNull(values.exclusivity_ends_on)
      : null,
    operation: values.operation,
    furnished: isRenta && values.furnished !== "" ? values.furnished : null,
    levels: toCount(values.levels),
    bedrooms: toCount(values.bedrooms),
    bathrooms: toCount(values.bathrooms),
    area_m2: toCount(values.area_m2),
    parking: toCount(values.parking),
    price: Number(values.price),
    status: values.status,
    responsable_sales_id: isAmk ? null : toIdentifier(values.responsable),
    responsable_is_amk: isAmk,
  };
};

/** The owner payload, or null when the owner block is empty (nothing to save). */
export const buildOwnerPayload = (
  values: OpcionFormValues,
): Pick<
  PropertyOptionOwner,
  "full_name" | "phone" | "email" | "notes"
> | null =>
  hasOwnerData(values)
    ? {
        full_name: values.owner_full_name.trim(),
        phone: blankToNull(values.owner_phone),
        email: blankToNull(values.owner_email),
        notes: blankToNull(values.owner_notes),
      }
    : null;

/**
 * Turns the list's `responsable` filter into the real columns (AMK means
 * `responsable_is_amk`, otherwise `responsable_sales_id`). Returns a new object.
 */
export const normalizeOptionListFilter = (
  filter: Record<string, unknown> | undefined,
): Record<string, unknown> => {
  const { responsable, ...rest } = filter ?? {};
  if (responsable == null || responsable === "") return rest;
  if (responsable === AMK_CHOICE) return { ...rest, responsable_is_amk: true };
  return { ...rest, responsable_sales_id: toIdentifier(String(responsable)) };
};

/** Server rejections are already Spanish sentences; pass them through. */
export const toOpcionErrorMessage = (error: unknown): string => {
  const message = error instanceof Error ? error.message : undefined;
  return message || OPCION_GENERIC_ERROR;
};
